import React from 'react';
import { Box, Button, Typography, IconButton } from '@mui/material';
import {
  LocationOn as LocationIcon,
  Facebook as FacebookIcon,
  Share as ShareIcon,
  WhatsApp as WhatsAppIcon,
  Telegram as TelegramIcon,
  Home as HomeIcon,
} from '@mui/icons-material';

// Simple date formatting function (replaces date-fns)
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = date.getDate().toString().padStart(2, '0');
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

// Format time from 24-hour to 12-hour AM/PM format
const formatTime = (timeString) => {
  if (!timeString) return '';
  
  // If already in AM/PM format, return as is
  if (timeString.toLowerCase().includes('am') || timeString.toLowerCase().includes('pm')) {
    return timeString;
  }
  
  // Handle HH:MM format (e.g., "14:30" or "14:30:00")
  const timeMatch = timeString.match(/(\d{1,2}):(\d{2})(?::\d{2})?/);
  if (!timeMatch) return timeString;
  
  let hours = parseInt(timeMatch[1], 10);
  const minutes = timeMatch[2];
  const ampm = hours >= 12 ? 'PM' : 'AM';
  
  // Convert to 12-hour format
  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'
  
  return `${hours}:${minutes} ${ampm}`;
};

const ObituaryPreview = ({ obituary, editForm }) => {
  // Use editForm data if available, otherwise fall back to obituary data
  const displayData = {
    deceasedName: editForm?.deceasedName || obituary?.deceasedName,
    dateOfDeath: editForm?.dateOfDeath || obituary?.dateOfDeath,
    // Use photo from obituary if available (set by PublicObituaryViewPage via apiClient.getObituaryPhotoUrl)
    // Otherwise fall back to the old photo field or default placeholder
    photo: obituary?.photo || (obituary?.photoId || obituary?.photoUrl 
      ? null 
      : 'https://cfn.hmdev.cloud/thumb/media/2025/04/firefly-a-three-quarter-portrait-of-a-well-groomed-old-lady-well-dressed-with-a-lighter-complexion-2.jpg?w=267&h=198'),
    eulogy: editForm?.eulogy || obituary?.eulogy || obituary?.description,
    services: editForm?.services || obituary?.services,
    liveStreamUrl: editForm?.liveStreamUrl || obituary?.liveStreamUrl,
    facebookPostUrl: editForm?.facebookPostUrl || obituary?.facebookPostUrl,
    condolenceUrl: editForm?.condolenceUrl || obituary?.condolenceUrl,
    funeralDirector: obituary?.funeralDirector
  };

  // Helper function to get service display data
  const getServiceDisplayData = (service) => {
    if (editForm?.services) {
      // editForm services structure
      return {
        serviceType: service.serviceType,
        date: service.date,
        time: service.time,
        venue: service.venueName || service.venue,
        address: service.address || service.location
      };
    } else {
      // obituary services structure
      return {
        serviceType: service.type || service.name,
        date: service.startDate || service.date,
        time: service.startTime || service.time,
        venue: service.venueName || service.venue || service.location,
        address: service.address || service.location
      };
    }
  };

  // Helper function for container-based responsive sizing
  const getResponsiveSize = (sizes) => {
    // Use CSS custom properties for container-based responsive sizing
    return {
      '@container (max-width: 400px)': { fontSize: sizes.small },
      '@container (min-width: 401px) and (max-width: 600px)': { fontSize: sizes.medium },
      '@container (min-width: 601px)': { fontSize: sizes.large }
    };
  };

  return (
    <Box sx={{ 
      width: '100%',
      p: 0,
      containerType: 'inline-size' // Enable container queries
    }}>
      {/* Top Section */}
      <Box sx={{ 
        textAlign: 'center',
        position: 'relative',
        p: '30px 0 20px'
      }}>
        <Box sx={{ 
          position: 'relative',
          display: 'inline-block',
          width: '100%'
        }}>
          <Box sx={{ 
            maxWidth: '100%',
            borderRadius: '20px',
            border: '3px solid rgba(132,132,132,0.5)',
            background: 'radial-gradient(circle, rgba(255,255,255,1) 0%, rgba(255,255,255,1) 65%, rgba(232,232,232,1) 100%)',
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative'
          }}>
            <img 
              src="https://cfn.hmdev.cloud/storage/media/2019/notices/top_bg.png" 
              alt="Top Background"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '20px'
              }}
            />
            <Box sx={{ 
              position: 'relative',
              zIndex: 1,
              textAlign: 'center'
            }}>
              <Typography sx={{ 
                color: '#000',
                fontSize: '64px',
                letterSpacing: '1px',
                fontFamily: 'Playfair Display, serif',
                p: 0,
                mb: 1,
                ...getResponsiveSize({
                  small: '20px',
                  medium: '40px', 
                  large: '64px'
                })
              }}>
                Bryson &amp; Son
                <br />
                <span style={{
                  color: '#575757',
                  fontSize: '0.7em',
                  letterSpacing: '0px',
                  fontWeight: '100',
                  fontFamily: 'Playfair Display, serif',
                  textTransform: 'uppercase'
                }}>
                  Funeral Home
                </span>
              </Typography>
              <Typography sx={{ 
                color: '#575757',
                fontSize: '1em',
                letterSpacing: '0px',
                pt: '15px',
                fontFamily: 'Arial, Helvetica, sans-serif',
                fontWeight: '100',
                textTransform: 'uppercase',
                ...getResponsiveSize({
                  small: '0.5em',
                  medium: '0.7em',
                  large: '1em'
                })
              }}>
                Celebrating Lives &amp; Caring for Families
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Profile Section */}
      <Box sx={{ p: '30px 0' }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h2" sx={{ 
            fontSize: '36px',
            color: '#000',
            pb: '15px',
            letterSpacing: '-1px',
            fontWeight: '700',
            fontFamily: 'Raleway, sans-serif',
            ...getResponsiveSize({
              small: '18px',
              medium: '28px',
              large: '36px'
            })
          }}>
            With deep sadness we<br /> announce that
          </Typography>
        </Box>
        
        {/* Photo */}
        {displayData.photo && (
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box sx={{ pt: '30px' }}>
              <Box sx={{ 
                boxShadow: '0 0 10px 0 #888',
                maxWidth: '550px',
                width: '66%',
                borderRadius: '15px',
                overflow: 'hidden',
                mx: 'auto'
              }}>
                <img 
                  src={displayData.photo} 
                  id="user_img" 
                  alt={displayData.deceasedName}
                  style={{ width: '100%', height: 'auto', display: 'block' }}
                />
              </Box>
            </Box>
          </Box>
        )}
        
        {/* Deceased Name and Date */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h1" sx={{ 
            fontFamily: 'Playfair Display, serif',
            fontWeight: '700',
            letterSpacing: '-1px',
            fontSize: '58px',
            p: '2.5rem 0',
            color: '#000',
            ...getResponsiveSize({
              small: '20px',
              medium: '40px',
              large: '58px'
            })
          }}>
            {displayData.deceasedName}
          </Typography>
          {displayData.dateOfDeath && (
            <Typography variant="body1" sx={{ 
              letterSpacing: '-1px',
              lineHeight: '1.3',
              mx: 'auto',
              maxWidth: '800px',
              fontSize: '32px',
              color: '#000',
              fontFamily: 'Raleway, sans-serif',
              ...getResponsiveSize({
                small: '18px',
                medium: '24px',
                large: '32px'
              })
            }}>
              Passed away on {formatDate(displayData.dateOfDeath)}
            </Typography>
          )}
        </Box>

        {/* Eulogy/Description */}
        {displayData.eulogy && (
          <Box sx={{ textAlign: 'center', mb: 4, maxWidth: '800px', mx: 'auto' }}>
            <Typography variant="body1" sx={{ 
              fontSize: '22px',
              lineHeight: '1.8',
              color: '#000',
              fontFamily: 'Raleway, sans-serif',
              whiteSpace: 'pre-wrap',
              ...getResponsiveSize({
                small: '16px',
                medium: '18px',
                large: '22px'
              })
            }}>
              {displayData.eulogy}
            </Typography>
          </Box>
        )}

        {/* Funeral Services Section */}
        {displayData.services && displayData.services.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="body1" sx={{ 
                mt: '25px',
                fontSize: '32px',
                color: '#000',
                fontFamily: 'Raleway, sans-serif',
                ...getResponsiveSize({
                  small: '18px',
                  medium: '24px',
                  large: '32px'
                })
              }}>
                The Funeral details are below
              </Typography>
            </Box>
            
            {displayData.services.map((service, index) => {
              const serviceData = getServiceDisplayData(service);
              if (!serviceData.date) return null;
              
              return (
                <Box key={index} sx={{ 
                  bgcolor: '#f4f4ef',
                  p: '20px 15px',
                  boxShadow: 'inset 0px 0px 150px rgba(0,0,0,0.2)',
                  mb: '20px',
                  textAlign: 'center',
                }}>
                  <Typography variant="h1" sx={{ 
                    fontSize: '58px',
                    lineHeight: '1.5',
                    color: '#000',
                    fontFamily: 'Raleway, sans-serif',
                    mb: 2,
                    ...getResponsiveSize({
                      small: '20px',
                      medium: '40px',
                      large: '58px'
                    })
                  }}>
                    {serviceData.serviceType}
                  </Typography>
                  
                  <Typography variant="body1" sx={{ 
                    fontSize: '32px',
                    color: '#000',
                    fontFamily: 'Raleway, sans-serif',
                    mb: 1,
                    ...getResponsiveSize({
                      small: '18px',
                      medium: '24px',
                      large: '32px'
                    })
                  }}>
                    {formatDate(serviceData.date)}
                  </Typography>
                  
                  {serviceData.time && (
                    <Typography variant="body1" sx={{ 
                      fontSize: '22px',
                      mb: 0.5,
                      ...getResponsiveSize({
                        small: '16px',
                        medium: '18px',
                        large: '22px'
                      })
                    }}>
                      {formatTime(serviceData.time)}
                    </Typography>
                  )}
                  
                  {serviceData.venue && (
                    <Typography variant="body1" sx={{ 
                      fontSize: '22px',
                      mb: 0.5,
                      ...getResponsiveSize({
                        small: '16px',
                        medium: '18px',
                        large: '22px'
                      })
                    }}>
                      {serviceData.venue}
                    </Typography>
                  )}
                  
                  {serviceData.address && serviceData.address !== serviceData.venue && (
                    <Typography variant="body1" sx={{ 
                      fontSize: '22px',
                      mb: 2,
                      ...getResponsiveSize({
                        small: '16px',
                        medium: '18px',
                        large: '22px'
                      })
                    }}>
                      {serviceData.address}
                    </Typography>
                  )}
                  
                  {/* Google Maps Embed */}
                  {(serviceData.address || serviceData.venue) && (
                    <Box sx={{ 
                      width: '100%',
                      maxWidth: '600px',
                      height: '300px',
                      mx: 'auto',
                      mb: 2,
                      borderRadius: '8px',
                      overflow: 'hidden',
                      border: '2px solid #ddd'
                    }}>
                      <iframe
                        width="100%"
                        height="100%"
                        style={{ border: 0 }}
                        loading="lazy"
                        allowFullScreen
                        referrerPolicy="no-referrer-when-downgrade"
                        src={`https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_PLACES_API_KEY || ''}&q=${encodeURIComponent(serviceData.address || serviceData.venue)}`}
                        title={`Map of ${serviceData.address || serviceData.venue}`}
                      />
                    </Box>
                  )}
                  
                      {/* Get Directions Link */}
                      {(serviceData.address || serviceData.venue) && (
                        <Box sx={{ mt: 1, mb: 1 }}>
                          <Box 
                            component="a"
                            href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(serviceData.address || serviceData.venue)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ 
                              color: '#000',
                              textDecoration: 'none',
                              display: 'inline-flex',
                              alignItems: 'center',
                              fontSize: '18px',
                              '&:hover': {
                                textDecoration: 'underline'
                              }
                            }}
                          >
                            <LocationIcon sx={{ mr: 1, fontSize: '24px' }} />
                            <Box component="span">Get Directions</Box>
                          </Box>
                        </Box>
                      )}
                </Box>
              );
            })}
          </Box>
        )}

        <Box sx={{ 
          textAlign: 'center', 
          p: 3, 
          color: '#000',
          mb: 4
        }}>
          <Typography variant="h1" sx={{ 
            fontSize: '58px',
            lineHeight: '1.5',
            color: '#000',
            fontFamily: 'Raleway, sans-serif',
            mb: 2,
            ...getResponsiveSize({
              small: '20px',
              medium: '40px',
              large: '58px'
            })
          }}>
            REACH OUT
          </Typography>
          <Typography variant="body1" sx={{ 
            fontSize: '22px',
            mb: 2,
            ...getResponsiveSize({
              small: '16px',
              medium: '18px',
              large: '22px'
            })
          }}>
            With the passing of {displayData.deceasedName}, this is a meaningful time to reach out to the family and offer support. Even a brief call or message can bring real comfort.
          </Typography>
          <Typography variant="body1" sx={{ 
            fontSize: '22px',
            mb: 2,
            ...getResponsiveSize({
              small: '16px',
              medium: '18px',
              large: '22px'
            })
          }}>
            A personalized sympathy card can also be a heartfelt way to show you care.
          </Typography>

          <Button 
            variant="contained" 
            color="primary" 
            sx={{ 
              mt: 2, 
              fontSize: '1.2em', 
              bgcolor: '#870505', 
              '&:hover': { bgcolor: '#650404' }, 
              margin: '3em 0 0 0' 
            }}
          >
            Send a Card
          </Button>
        </Box>

        {/* Funeral Director Info */}
        {displayData.funeralDirector && (
          <Box sx={{ 
            textAlign: 'center', 
            p: 3, 
            bgcolor: '#f4f4ef',
            mb: 4
          }}>
            <Typography variant="body1" sx={{ mb: 2, fontSize: '17px', color: '#4C4F54' }}>
              This Obituary was published by:
            </Typography>
            <Typography variant="h5" sx={{ 
              mb: 2,
              fontSize: '32px',
              fontWeight: '700',
              color: '#000',
              fontFamily: 'Raleway, sans-serif',
              ...getResponsiveSize({
                small: '20px',
                medium: '26px',
                large: '32px'
              })
            }}>
              {displayData.funeralDirector.name || 'Bryson & Son'}
            </Typography>
            <Typography variant="body1" sx={{ 
              mb: 2, 
              fontSize: '17px', 
              color: '#4C4F54',
              ...getResponsiveSize({
                small: '14px',
                medium: '15px',
                large: '17px'
              })
            }}>
              Funeral Home
            </Typography>
            {displayData.funeralDirector.address && (
              <Typography variant="body1" sx={{ mb: 2, fontSize: '17px', color: '#4C4F54' }}>
                {displayData.funeralDirector.address}
              </Typography>
            )}
            {displayData.funeralDirector.phone && (
              <Typography variant="body1" sx={{ mb: 3, fontSize: '17px', color: '#4C4F54' }}>
                Tel: {displayData.funeralDirector.phone}
              </Typography>
            )}
            
            {displayData.funeralDirector.website && (
              <Button
                component="a"
                href={displayData.funeralDirector.website}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                startIcon={<HomeIcon />}
                sx={{
                  bgcolor: '#000',
                  color: '#fff',
                  p: '15px 20px',
                  mt: '20px',
                  '&:hover': {
                    bgcolor: '#333'
                  }
                }}
              >
                Visit Website
              </Button>
            )}
          </Box>
        )}

        {/* Share Section */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="body1" sx={{ 
            bgcolor: '#000',
            color: '#fff',
            p: '20px',
            textAlign: 'center',
            fontFamily: 'Verdana, sans-serif',
            fontWeight: '900',
            mb: 3
          }}>
            THE FAMILY GREATLY APPRECIATES <br />
            YOUR ASSISTANCE IN SHARING THIS OBITUARY
          </Typography>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            <IconButton
              component="a"
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ 
                bgcolor: '#1877F2',
                color: '#fff',
                '&:hover': { bgcolor: '#1565C0' },
                width: 50,
                height: 50
              }}
              title="Share on Facebook"
            >
              <FacebookIcon />
            </IconButton>
            <IconButton
              component="a"
              href={`sms:?body=${encodeURIComponent(window.location.href)}`}
              sx={{ 
                bgcolor: '#34C759',
                color: '#fff',
                '&:hover': { bgcolor: '#28A745' },
                width: 50,
                height: 50
              }}
              title="Share via iMessage/SMS"
            >
              <ShareIcon />
            </IconButton>
            <IconButton
              component="a"
              href={`https://wa.me/?text=${encodeURIComponent(window.location.href)}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ 
                bgcolor: '#25D366',
                color: '#fff',
                '&:hover': { bgcolor: '#128C7E' },
                width: 50,
                height: 50
              }}
              title="Share on WhatsApp"
            >
              <WhatsAppIcon />
            </IconButton>
            <IconButton
              component="a"
              href={`https://t.me/share/url?url=${encodeURIComponent(window.location.href)}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ 
                bgcolor: '#0088cc',
                color: '#fff',
                '&:hover': { bgcolor: '#006BA1' },
                width: 50,
                height: 50
              }}
              title="Share on Telegram"
            >
              <TelegramIcon />
            </IconButton>
            <IconButton
              component="a"
              href={`https://www.facebook.com/dialog/send?link=${encodeURIComponent(window.location.href)}&app_id=YOUR_APP_ID`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ 
                bgcolor: '#0084FF',
                color: '#fff',
                '&:hover': { bgcolor: '#0066CC' },
                width: 50,
                height: 50
              }}
              title="Share on Facebook Messenger"
            >
              <FacebookIcon />
            </IconButton>
          </Box>
        </Box>

      </Box>
    </Box>
  );
};

export default ObituaryPreview;

